// https://www.npmjs.com/package/nanogl

/** A webgl rendering context. */
export type GLContext = WebGLRenderingContext | WebGL2RenderingContext;

/**
 * Know whether the webgl context is webgl2 or not.
 * @param {GLContext} context The webgl context to test
 */
export function isWebgl2(context: GLContext): context is WebGL2RenderingContext {
  return (<WebGL2RenderingContext>context).fenceSync !== undefined;
}

interface CompilationContext {
  texIndex: number;
  ublockIndex: number;
}

let _UID: number = 0;

/**
 * This class provides provides shader compilation and webgl program linking functionality.
 * It also give you convenient access to active uniforms and attributes.
 */
export class Program {
  /** Can be set to true to check and log compilation and linking errors */
  static debug: boolean = false;
  /** The webgl context this Program belongs to */
  readonly gl: GLContext;

  /** The underlying webgl program */
  readonly program: WebGLProgram;
  /** The underlying webgl vertex shader */
  readonly vShader: WebGLShader;
  /** The underlying webgl fragment shader */
  readonly fShader: WebGLShader;

  /** The list of uniform & attribute names */
  protected dyns: string[];

  /** Whether the program is ready to use or not */
  ready: boolean;
  /** Unique id for the program */
  _uid: number;
  /** Unique id for the program that updates every time it is compiled */
  _cuid: number;

  [k: string]: any;

  /**
   * You can pass optional shader code to immediatly compile shaders or compile them later with {@link Program#compile}.
   * @param {GLContext} gl  The webgl context this Program belongs to
   * @param {String} [vert] The vertex shader code
   * @param {String} [frag] The fragment shader code
   * @param {String} [defs] A string to prepend to both fragment and vertex shader code
   */
  constructor(gl: GLContext, vert?: string, frag?: string, defs?: string) {
    this.gl = gl;
    this.program = <WebGLProgram>gl.createProgram();
    this.vShader = <WebGLShader>gl.createShader(gl.VERTEX_SHADER);
    this.fShader = <WebGLShader>gl.createShader(gl.FRAGMENT_SHADER);
    this.dyns = [];
    this.ready = false;
    gl.attachShader(this.program, this.vShader);
    gl.attachShader(this.program, this.fShader);

    this._uid = _UID++ | 0;
    this._cuid = _UID++ | 0;

    if (vert !== undefined && frag !== undefined) {
      this.compile(vert, frag, defs);
    }
  }

  /**
   * Bind the underlying webgl program.
   * Shortcut for `gl.useProgram()`.
   */
  use() {
    if (!this.ready) {
      this._grabParameters();
    }
    this.gl.useProgram(this.program);
  }

  /**
   * Alias for {@link Program#use}.
   */
  bind() {
    this.use();
  }

  /**
   * Compile vertex and fragment shader, then link webgl program.
   * This method can be safely called several times.
   * @param {String} vert The vertex shader code
   * @param {String} frag The fragment shader code
   * @param {String} [prefix] A string to prepend to both fragment and vertex shader code
   */
  compile(vert: string, frag: string, prefix?: string): boolean {
    this.ready = false;

    prefix = prefix === undefined ? '' : prefix + '\n';

    const gl = this.gl;

    if (!(compileShader(gl, this.fShader, prefix + frag) && compileShader(gl, this.vShader, prefix + vert))) {
      return false;
    }

    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      Program.debug && warn(gl.getProgramInfoLog(this.program));
      return false;
    }

    // delete old accessors
    while (this.dyns.length > 0) {
      delete this[<string>this.dyns.pop()];
    }

    this._cuid = _UID++ | 0;

    return true;
  }

  /**
   * Delete all webgl objects related to this Program.
   */
  dispose() {
    if (this.gl !== null) {
      this.gl.deleteProgram(this.program);
      this.gl.deleteShader(this.fShader);
      this.gl.deleteShader(this.vShader);
    }
  }

  /*
   *  List all uniforms and attributes and create helper function on Program instance
   *  for a uniform vec3 uDirection;
   *     create a method
   *        program.uDirection( 1, 0, 0 );
   */
  /**
   * List all uniforms and attributes and create a helper function for each on Program instance.
   *
   * @example
   * For a uniform
   * ```glsl
   * uniform vec3 uDirection;
   * ```
   * it creates a method
   * ```js
   * program.uDirection(1, 0, 0);
   * ```
   */
  _grabParameters() {
    const gl = this.gl,
      prg = this.program;

    const context: CompilationContext = {
      texIndex: 0,
      ublockIndex: 0,
    };

    // Uniforms
    // ========

    const numUniforms: number = gl.getProgramParameter(prg, gl.ACTIVE_UNIFORMS);

    for (let uniformIndex = 0; uniformIndex < numUniforms; ++uniformIndex) {
      const uniform = gl.getActiveUniform(prg, uniformIndex);

      // safari 8.0 issue,
      // when recompiling shader and link the progam again, old uniforms are kept in ACTIVE_UNIFORMS count but return null here
      if (uniform === null) {
        gl.getError(); // also flush error
        continue;
      }

      let uName = uniform.name,
        n = uName.indexOf('[');

      if (n >= 0) {
        uName = uName.substring(0, n);
      }

      const uLocation = gl.getUniformLocation(prg, uniform.name);

      // in Webgl2 location can be null here if uniform is member of a uniform block
      if (uLocation !== null) {
        this[uName] = getUniformSetter(uniform.type, uLocation, gl, context);
        this.dyns.push(uName);
      }
    }

    // Attributes
    // ==========

    const numAttribs = gl.getProgramParameter(prg, gl.ACTIVE_ATTRIBUTES);

    for (let aIndex = 0; aIndex < numAttribs; ++aIndex) {
      const attribName = (<WebGLActiveInfo>gl.getActiveAttrib(prg, aIndex)).name;
      const aLocation = gl.getAttribLocation(prg, attribName);
      this[attribName] = getAttribAccess(aLocation);
      this.dyns.push(attribName);
    }

    // UniformBlock
    // ============

    if (isWebgl2(gl)) {
      const numBlocks = gl.getProgramParameter(prg, gl.ACTIVE_UNIFORM_BLOCKS);

      for (let blockIndex = 0; blockIndex < numBlocks; ++blockIndex) {
        const blockName = <string>gl.getActiveUniformBlockName(prg, blockIndex);
        this[blockName] = getUniformBufferSetFunction(blockIndex, gl, context);
        this.dyns.push(blockName);
      }
    }

    this.ready = true;
  }
}

/*
 * internal logs
 */
function warn(str: string | null) {
  console.warn(str);
}

// -------------------------------------------------
//                    UTILITIES
// -------------------------------------------------

/*
 * Shader logging utilities
 */

const __pads: Array<string> = ['', '   ', '  ', ' ', ''];

function appendLine(l: string, i: number): string {
  return __pads[String(i + 1).length] + (i + 1) + ': ' + l;
}

/*
 * Format shader code
 * add padded lines number
 */
function formatCode(shader: string): string {
  return shader
    .split('\n')
    .map(appendLine)
    .join('\n');
}

const ErrLineRegex = /^ERROR:\s?(\d+):(\d+)/;

function reportCompileError(infos: string, source: string) {
  const sourceLines = source.split('\n');
  infos = infos.split('\n').map((line) => {
    const rr = ErrLineRegex.exec(line);
    if (rr) { line += '\n  > ' + sourceLines[parseInt(rr[2]) - 1]; }
    return line;
  }).join('\n');
  source = formatCode(source);

  warn(infos);
  warn(source);
}


/*
 * Shader compilation utility
 */
function compileShader(gl: GLContext, shader: WebGLShader, code: string): boolean {
  gl.shaderSource(shader, code);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    Program.debug && reportCompileError(gl.getShaderInfoLog(shader)!, code);
    return false;
  }

  return true;
}




const USetFMap: Record<string, string> = {};
USetFMap[String(5126) /*FLOAT       */] = '1f';
USetFMap[String(35664) /*FLOAT_VEC2  */] = '2f';
USetFMap[String(35665) /*FLOAT_VEC3  */] = '3f';
USetFMap[String(35666) /*FLOAT_VEC4  */] = '4f';
USetFMap[String(35670) /*BOOL        */] =
  USetFMap[String(5124) /*INT         */] =
  USetFMap[String(35678) /*SAMPLER_2D  */] = USetFMap[String(35680) /*SAMPLER_CUBE*/] = USetFMap[String(35866) /*SAMPLER_2D_ARRAY*/] = '1i';
USetFMap[String(35671) /*BOOL_VEC2   */] = USetFMap[String(35667) /*INT_VEC2    */] = '2i';
USetFMap[String(35672) /*BOOL_VEC3   */] = USetFMap[String(35668) /*INT_VEC3    */] = '3i';
USetFMap[String(35673) /*BOOL_VEC4   */] = USetFMap[String(35669) /*INT_VEC4    */] = '4i';
USetFMap[String(35674) /*FLOAT_MAT2  */] = 'Matrix2f';
USetFMap[String(35675) /*FLOAT_MAT3  */] = 'Matrix3f';
USetFMap[String(35676) /*FLOAT_MAT4  */] = 'Matrix4f';

/*
 * Uniform upload utilities
 */

function getUniformSetFunctionName(type: number): string {
  return 'uniform' + USetFMap[String(type)];
}

/*
 * For a given uniform's type, return the proper setter function
 */
function getUniformSetter(type: number, location: WebGLUniformLocation, gl: GLContext, context: CompilationContext) {
  switch (type) {
    case gl.FLOAT_MAT2:
    case gl.FLOAT_MAT3:
    case gl.FLOAT_MAT4:
      return getMatrixSetFunction(type, location, gl, context);

    case gl.SAMPLER_2D:
    case gl.SAMPLER_CUBE:
    case 0x8b62: //gl.SAMPLER_2D_SHADOW:
    case 0x8b5f: //gl.SAMPLER_3D:
    case 0x8DC1: // gl.SAMPLER_2D_ARRAY:
      return getSamplerSetFunction(type, location, gl, context);

    default:
      return getUniformSetFunction(type, location, gl, context);
  }
}

/*
 * setter factory for vector uniforms
 * return a function wich take both array or arguments
 */
function getUniformSetFunction(
  type: number,
  location: WebGLUniformLocation,
  gl: GLContext,
  context: CompilationContext
) {
  context;
  const fname = getUniformSetFunctionName(type);
  return function (...args: number[] | [Float32List]) {
    if (args.length === 1 && (args[0] as []).length != undefined) {
      (gl as any)[fname + 'v'](location, args[0]);
    } else if (args.length > 0) {
      (gl as any)[fname](location, ...args);
    }
    return location;
  };
}

/*
 * setter factory for matrix uniforms
 */
function getMatrixSetFunction(
  type: number,
  location: WebGLUniformLocation,
  gl: GLContext,
  context: CompilationContext
) {
  context;
  const fname = getUniformSetFunctionName(type);
  return function () {
    if (arguments.length > 0 && arguments[0].length !== undefined) {
      const transpose = arguments.length > 1 ? !!arguments[1] : false;
      (gl as any)[fname + 'v'](location, transpose, arguments[0]);
    }
    return location;
  };
}

/*
 * setter factory for sampler uniforms
 */
function getSamplerSetFunction(
  type: number,
  location: WebGLUniformLocation,
  gl: GLContext,
  context: CompilationContext
) {
  const unit = context.texIndex++;
  return function () {
    if (arguments.length === 1) {
      if (arguments[0].bind !== undefined) {
        // is texture
        arguments[0].bind(unit);
        gl.uniform1i(location, unit);
      } else {
        gl.uniform1i(location, arguments[0]);
      }
    }
    return location;
  };
}

/*
 * setter factory for uniform buffers
 * can be
 *   f( buffer )
 *     buffer is bound to auto increment binding point unit and block binding reset to this point
 *   f( unit )
 *     only set the block binding point to given unit
 */
function getUniformBufferSetFunction(index: number, gl: WebGL2RenderingContext, context: CompilationContext) {
  const unit = context.ublockIndex++;
  return function (this: Program) {
    if (arguments.length === 1) {
      if (arguments[0] instanceof WebGLBuffer) {
        // is buffer
        gl.uniformBlockBinding(this.program, index, unit);
        gl.bindBufferBase(gl.UNIFORM_BUFFER, unit, arguments[0]);
      } else {
        gl.uniformBlockBinding(this.program, index, arguments[0]);
      }
    }
    return index;
  };
}

/*
 * getter factory for attributes
 */
function getAttribAccess(attrib: number) {
  return function () {
    return attrib;
  };
}
/**
 * This class is the base for all buffer classes.
 */
export abstract class BaseBuffer {
  /**
   * Bind the underlying webgl buffer.
   */
  abstract bind(): void;

  /**
   * Delete all webgl objects related to this BaseBuffer.
   */
  abstract dispose(): void;

  /**
   * Fill the webgl buffer with the given data. You can also pass a number instead to allocate the buffer to a given size.
   *   @param {BufferSource|GLsizeiptr} array The data to use, or a size
   */
  abstract data(array: BufferSource | GLsizeiptr): void;

  /**
   * Set part of the buffer with the given data, starting at an offset.
   *  @param {BufferSource} array The data to use
   *  @param {number} offset The offset (in bytes) where the data should start to be written
   */
  abstract subData(array: BufferSource, offset: number): void;

  /**
   * Shortcut to `gl.drawArrays`.
   *   @param {GLenum} mode The type of primitive to draw (`GL_TRIANGLE`, `GL_POINTS` etc)
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  abstract draw(mode: GLenum, count?: number, offset?: number): void;

  /**
   * Shortcut to `gl.drawArrays(gl.POINTS, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawPoints(count?: number, offset?: number) {
    this.draw(0 /* POINTS         */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.LINES, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawLines(count?: number, offset?: number) {
    this.draw(1 /* LINES          */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.LINE_LOOP, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawLineLoop(count?: number, offset?: number) {
    this.draw(2 /* LINE_LOOP      */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.LINE_STRIP, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawLineStrip(count?: number, offset?: number) {
    this.draw(3 /* LINE_STRIP     */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.TRIANGLES, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawTriangles(count?: number, offset?: number) {
    this.draw(4 /* TRIANGLES      */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.TRIANGLE_STRIP, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawTriangleStrip(count?: number, offset?: number) {
    this.draw(5 /* TRIANGLE_STRIP */, count, offset);
  }

  /**
   * Shortcut to `gl.drawArrays(gl.TRIANGLE_FAN, offset, count)`.
   *   @param {number} [count] The number of elements to draw (the full buffer is used if omited)
   *   @param {number} [offset=0] The position in the buffer to start from
   */
  drawTriangleFan(count?: number, offset?: number) {
    this.draw(6 /* TRIANGLE_FAN   */, count, offset);
  }
}
/**
 * Know whether the value is a buffer source or not.
 * You can use it for values that can be either a size or a buffer source.
 * @param {GLsizeiptr|BufferSource} val The value to test
 */
export function isBufferSource(val: GLsizeiptr | BufferSource): val is BufferSource {
  return (<BufferSource>val).byteLength !== undefined;
}

/**
 * Compute texture filtering enum from parameters.
 * Will return one of the following :
 * - `NEAREST`
 * - `LINEAR`
 * - `NEAREST_MIPMAP_NEAREST`
 * - `LINEAR_MIPMAP_NEAREST`
 * - `NEAREST_MIPMAP_LINEAR`
 * - `LINEAR_MIPMAP_LINEAR`
 * @param {boolean} [smooth=false] Use linear filtering or not
 * @param {boolean} [mipmap=false] Enable mipmapping or not
 * @param {boolean} [miplinear=false] Use linear mipmapping or not
 */
export function getTextureFiltering(smooth: boolean, mipmap: boolean, miplinear: boolean): GLenum {
  return 0x2600 | +smooth | (+mipmap << 8) | (+(mipmap && miplinear) << 1);
}

/** The size in bytes if a component. */
export type ComponentSize = 1 | 2 | 4

/**
 * Get the component size in bytes from a data type.
 * @param {GLenum} type The type of data (`GL_FLOAT`, `GL_SHORT`, etc.)
 */
export function getComponentSize(type: GLenum): ComponentSize {
  switch (type) {
    case 0x1400: //gl.BYTE:
    case 0x1401: //gl.UNSIGNED_BYTE:
      return 1;
    case 0x1402: //gl.SHORT:
    case 0x1403: //gl.UNSIGNED_SHORT:
      return 2;
    case 0x1404: //gl.INT:
    case 0x1405: //gl.UNSIGNED_INT:
    case 0x1406: //gl.FLOAT:
      return 4;
    default:
      throw new Error(`unknown type ${type}`);
  }
}

/*
 * GL_ARRAY_BUFFER */
const GL_ARRAY_BUFFER = 0x8892;

/** The definition for an attribute in a buffer. */
export interface AttributeDef {
  /** The attribute name */
  name: string;
  /** The type of data (`GL_FLOAT`, `GL_SHORT`, etc.) */
  type: GLenum;
  /** The size of the attribute (`1` for a number, `2` for a vec2, etc.) */
  size: number;
  /** The offset in bytes of the attribute data in the buffer */
  offset: number;
  /** Whethere the data must be normalized or not */
  normalize: boolean;
  /**
   * The number of bytes for each vertex in the buffer.
   * If not set, the stride of the buffer is used.
   */
  stride: number;
}

/**
 * This class manages ARRAY_BUFFER type buffers.
 * @extends {BaseBuffer}
 */
export class ArrayBuffer extends BaseBuffer {
  /** The webgl context this ArrayBuffer belongs to */
  readonly gl: GLContext;
  /** The webgl buffer this ArrayBuffer writes to */
  readonly buffer: WebGLBuffer;

  /** The usage hint for this buffer */
  usage: GLenum;
  /** The number of bytes for each vertex in this buffer  */
  stride: number;
  /** The length in bytes of the buffer */
  byteLength: number;
  /** The number of vertices in the buffer data */
  length: number;
  /** The attributes declared for this buffer */
  attribs: AttributeDef[];

  /**
    * @param {GLContext} gl  The webgl context this ArrayBuffer belongs to
    * @param {BufferSource|GLsizeiptr} [data]  The data to fill the buffer with, or the size (in bytes)
    * @param {GLenum} [usage=GL_STATIC_DRAW] The usage hint for this buffer (`GL_STATIC_DRAW`, `GL_DYNAMIC_DRAW`, etc.)
    * @param {WebGLBuffer} [glbuffer] A WebGLBuffer to use instead of creating a new one
    */
  constructor(gl: GLContext, data?: GLsizeiptr | BufferSource, usage: GLenum = gl.STATIC_DRAW, glbuffer?: WebGLBuffer) {
    super();

    this.gl = gl;
    this.usage = usage;

    this.buffer = (glbuffer !== undefined) ? glbuffer : <WebGLBuffer>gl.createBuffer();

    this.attribs = [];
    this.stride = 0;
    this.byteLength = 0;
    this.length = 0;

    if (data) {
      this.data(data);
    }
  }

  bind() {
    this.gl.bindBuffer(GL_ARRAY_BUFFER, this.buffer);
  }

  /**
   * Add attribute declaration for this buffer. Once the attributes declared, the buffer can be linked to
   * the program's attributes using {@link ArrayBuffer#attribPointer}.
   *  @param {string} name The name of the program's attribute
   *  @param {number} size The size of the attribute (`1` for a number, `2` for a vec2, etc.)
   *  @param {GLenum} type The type of data (`GL_FLOAT`, `GL_SHORT`, etc.)
   *  @param {boolean} [normalize=false] Indicate if the data must be normalized
   */
  attrib(name: string, size: number, type: GLenum, normalize: boolean = false): this {
    this.attribs.push({
      name,
      type: 0 | type,
      size: 0 | size,
      normalize,
      offset: this.stride,
      stride: 0,
    });
    this.stride += getComponentSize(type) * size;
    this._computeLength();
    return this;
  }

  data(array: BufferSource | GLsizeiptr) {
    const gl = this.gl;
    gl.bindBuffer(GL_ARRAY_BUFFER, this.buffer);
    gl.bufferData(GL_ARRAY_BUFFER, array as any, this.usage);
    gl.bindBuffer(GL_ARRAY_BUFFER, null);

    this.byteLength = isBufferSource(array) ? array.byteLength : array;
    this._computeLength();
  }

  subData(array: BufferSource, offset: number) {
    const gl = this.gl;
    gl.bindBuffer(GL_ARRAY_BUFFER, this.buffer);
    gl.bufferSubData(GL_ARRAY_BUFFER, offset, array);
    gl.bindBuffer(GL_ARRAY_BUFFER, null);
  }

  /**
   * Link the given program attributes to this buffer. You should first declare attributes using {@link ArrayBuffer#attrib}
   * before calling this method.
   *   @param {Program} program The program to link
   */
  attribPointer(program: Program) {
    const gl = this.gl;
    gl.bindBuffer(GL_ARRAY_BUFFER, this.buffer);

    for (let i = 0; i < this.attribs.length; i++) {
      const attrib = this.attribs[i];

      if (program[attrib.name] !== undefined) {
        const aLocation = program[attrib.name]();
        gl.enableVertexAttribArray(aLocation);
        gl.vertexAttribPointer(aLocation, attrib.size, attrib.type, attrib.normalize, attrib.stride || this.stride, attrib.offset);
      }
    }
  }

  /**
   * Shortcut to `gl.drawArrays`.
   *   @param {GLenum} mode The type of primitive to draw (`GL_TRIANGLE`, `GL_POINTS` etc)
   *   @param {uint} [count=this.length] The number of vertices to draw (the full buffer is used if omited)
   *   @param {uint} [offset=0] The position of the first vertex to draw
   */
  draw(mode: GLenum, count: number = this.length, offset: number = 0) {
    this.gl.drawArrays(mode, offset, 0 | count);
  }

  dispose() {
    this.gl.deleteBuffer(this.buffer);
  }

  /**
   * Compute the number of vertices in the buffer data.
   */
  _computeLength() {
    if (this.stride > 0) {
      this.length = this.byteLength / this.stride;
    }
  }
}
